/**
 * Expense categories (on payment vouchers) and income categories (on
 * receipt vouchers), each list edited in place: add, rename, switch off,
 * delete. Deleting one leaves its vouchers uncategorised, never deleted.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import RowActions from "components/RowActions";
import { api, problemOf } from "./api";

function CategoryList({ type, title, rows, onChanged }) {
  const [name, setName] = useState("");
  // {id, name} while a row is being renamed
  const [renaming, setRenaming] = useState(null);
  const [error, setError] = useState("");

  const run = async (fn) => {
    setError("");
    try {
      await fn();
      onChanged();
      return true;
    } catch (e) {
      setError(problemOf(e, "Could not save"));
      return false;
    }
  };

  const add = async () => {
    if (!name.trim()) return;
    if (await run(() => api.post("/categories", { name, type }))) setName("");
  };

  const rename = async () => {
    // Enter saves and the field's blur then fires too - only the first counts
    const row = renaming && rows.find((r) => r.id === renaming.id);
    if (!row) return;
    if (row.name === renaming.name.trim()) {
      setRenaming(null);
      return;
    }
    if (
      await run(() =>
        api.put(`/categories/${row.id}`, { name: renaming.name, type, is_active: row.is_active })
      )
    )
      setRenaming(null);
  };

  const remove = (row) => {
    const note = row.vouchers ? ` ${row.vouchers} voucher(s) will be left without a category.` : "";
    // eslint-disable-next-line no-alert
    if (window.confirm(`Delete "${row.name}"?${note}`)) run(() => api.del(`/categories/${row.id}`));
  };

  return (
    <SuiBox
      flex="1 1 380px"
      p={2}
      sx={({ palette, borders, functions: { rgba } }) => ({
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        borderRadius: borders.borderRadius.lg,
      })}
    >
      <SuiTypography variant="h6" mb={1.5}>
        {title}
      </SuiTypography>

      <SuiBox display="flex" gap={1} mb={1.5}>
        <TextField
          size="small"
          fullWidth
          placeholder="New category..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <Button variant="contained" color="primary" onClick={add}>
          Add
        </Button>
      </SuiBox>

      {error && (
        <SuiTypography variant="caption" color="error" display="block" mb={1}>
          {error}
        </SuiTypography>
      )}

      {rows.map((r) => (
        <SuiBox
          key={r.id}
          display="flex"
          alignItems="center"
          gap={1}
          py={0.75}
          sx={({ palette, functions: { rgba } }) => ({
            borderBottom: `1px solid ${rgba(palette.dark.main, 0.06)}`,
          })}
        >
          {renaming?.id === r.id ? (
            <TextField
              size="small"
              autoFocus
              fullWidth
              value={renaming.name}
              onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") rename();
                if (e.key === "Escape") setRenaming(null);
              }}
              onBlur={rename}
            />
          ) : (
            <SuiTypography
              sx={{ flex: 1, fontSize: 14, fontWeight: 600, opacity: r.is_active ? 1 : 0.45 }}
            >
              {r.name}
              <SuiTypography component="span" sx={{ fontSize: 12, opacity: 0.55, ml: 1 }}>
                {r.vouchers ? `${r.vouchers} vouchers` : ""}
              </SuiTypography>
            </SuiTypography>
          )}
          <Tooltip title={r.is_active ? "Active - offered on vouchers" : "Switched off"}>
            <Switch
              size="small"
              checked={r.is_active}
              onChange={(e) =>
                run(() =>
                  api.put(`/categories/${r.id}`, {
                    name: r.name,
                    type,
                    is_active: e.target.checked,
                  })
                )
              }
            />
          </Tooltip>
          <RowActions
            actions={[
              {
                icon: "edit",
                label: "Rename",
                onClick: () => setRenaming({ id: r.id, name: r.name }),
              },
              { icon: "delete", label: "Delete", tone: "danger", onClick: () => remove(r) },
            ]}
          />
        </SuiBox>
      ))}
      {rows.length === 0 && (
        <SuiTypography variant="caption" sx={{ opacity: 0.6 }}>
          None yet
        </SuiTypography>
      )}
    </SuiBox>
  );
}

CategoryList.propTypes = {
  type: PropTypes.oneOf(["expense", "income"]).isRequired,
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func.isRequired,
};

function CategoriesTab({ categories, onChanged }) {
  return (
    <SuiBox px={3} py={2}>
      <SuiTypography variant="h6" mb={2}>
        Categories · فئات المصاريف والإيرادات
      </SuiTypography>
      <SuiBox display="flex" gap={3} flexWrap="wrap">
        <CategoryList
          type="expense"
          title="Expense categories · فئات المصاريف"
          rows={categories.filter((c) => c.type === "expense")}
          onChanged={onChanged}
        />
        <CategoryList
          type="income"
          title="Income categories · فئات الإيرادات"
          rows={categories.filter((c) => c.type === "income")}
          onChanged={onChanged}
        />
      </SuiBox>
    </SuiBox>
  );
}

CategoriesTab.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChanged: PropTypes.func.isRequired,
};

export default CategoriesTab;
